import {UpcomingEvent} from '@/apiTypes/event';
import {HIDDEN_EVENT_TYPES} from '@/constants/eventTypes';
import {prismaClient} from '@/helpers/prismaClient.server';
import {stripHtmlToText} from '@/helpers/sanitizeHtml.server';
import {createServerFn} from '@tanstack/react-start';
import {z} from 'zod';

/** Number of events shown in the homepage "Coming up" row */
const UPCOMING_EVENTS_LIMIT = 4;

const LatLng = z.object({lat: z.number(), lng: z.number()});

const UpcomingEventsInput = z.object({
  southWest: LatLng,
  northEast: LatLng,
});

/**
 * Fetches the next few dated events within the given bounds, soonest first.
 * Editorial HTML is reduced to a plain-text excerpt so the client never
 * needs to parse rich text for the card preview.
 */
export const getUpcomingEvents = createServerFn({method: 'GET'})
  .inputValidator((data: unknown) => {
    const result = UpcomingEventsInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid upcoming events input');
    }
    return result.data;
  })
  .handler(async ({data}) => {
    const {southWest, northEast} = data;
    const prisma = await prismaClient();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const events = await prisma.event.findMany({
      where: {
        archived: false,
        eventType: {notIn: HIDDEN_EVENT_TYPES},
        lat: {gte: southWest.lat, lte: northEast.lat},
        lng: {gte: southWest.lng, lte: northEast.lng},
        startDate: {gte: today},
      },
      orderBy: {startDate: 'asc'},
      take: UPCOMING_EVENTS_LIMIT,
      select: {
        id: true,
        name: true,
        eventType: true,
        discipline: true,
        startDate: true,
        endDate: true,
        location: true,
        heroUrl: true,
        editorialContent: true,
        editorialAuthor: {select: {firstName: true, avatarUrl: true}},
        organizer: {select: {name: true}},
        routes: {
          select: {distance_meters: true, elevation_gain_meters: true},
        },
      },
    });

    return z.array(UpcomingEvent).parse(
      events.map(({editorialContent, ...event}) => ({
        ...event,
        editorialExcerpt: stripHtmlToText(editorialContent),
      }))
    );
  });
